import type { ReactNode } from "react"
import { memo, useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { MAX_SPEC_BYTES as MAX_FILE_BYTES } from "@productlane/lib/src/api-reference/types"
import { Button, Dialog, Input } from "@productlane/ui"

import type { SpecSourceKind } from "./api-reference-source-fields"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import {
	API_REFERENCE_CREATED,
	NEW_API_REFERENCE,
} from "./api-reference-defaults"
import {
	SPEC_TOO_LARGE,
	SpecFileButton,
	SpecSourceKindControl,
	uploadSpecFile,
} from "./api-reference-source-fields"
import { useDocsTabs } from "./use-docs-tabs"

type Source =
	| { type: "url"; url: string }
	| { type: "file"; fileName: string; uploadKey: string }

interface Preview {
	title: string
	apiVersion: string
	openapi: string
	operations: number
	events: number
	warnings: string[]
}

export const createLabel = (pages: number) =>
	`Create ${pages} page${pages === 1 ? "" : "s"}`

export const NewApiReferenceDialog = memo(() => {
	const navigate = useNavigate()
	const { isNewApiReferenceOpen: isOpen, setNewApiReferenceOpen } =
		useDocsTabDialogs()
	const { portalInstanceId, setActiveTabId } = useDocsTabs()
	const [kind, setKind] = useState<SpecSourceKind>("url")
	const [url, setUrl] = useState("")
	const [file, setFile] = useState<File | null>(null)
	const [name, setName] = useState(NEW_API_REFERENCE.name)
	const [slug, setSlug] = useState(NEW_API_REFERENCE.slug)
	const [checked, setChecked] = useState<{
		source: Source
		preview: Preview
	} | null>(null)
	const [isUploading, setIsUploading] = useState(false)

	const utils = api.useUtils()
	const uploadUrl = api.apiReference.uploadUrl.useMutation()
	const preview = api.apiReference.preview.useMutation()
	const create = api.apiReference.create.useMutation()

	useEffect(() => {
		if (isOpen) return
		setKind("url")
		setUrl("")
		setFile(null)
		setName(NEW_API_REFERENCE.name)
		setSlug(NEW_API_REFERENCE.slug)
		setChecked(null)
	}, [isOpen])

	const close = () => setNewApiReferenceOpen(false)

	const check = async () => {
		try {
			let source: Source
			if (kind === "url") {
				if (!/^https?:\/\//.test(url.trim())) {
					toast.info({
						title: "Enter the full spec URL, starting with https://",
					})
					return
				}
				source = { type: "url", url: url.trim() }
			} else {
				if (!file) {
					toast.info({ title: "Choose a JSON or YAML file first" })
					return
				}
				setIsUploading(true)
				source = {
					type: "file",
					...(await uploadSpecFile(file, (input) =>
						uploadUrl.mutateAsync(input),
					)),
				}
			}
			const result = await preview.mutateAsync({ source })
			setChecked({ source, preview: result })
		} catch (error) {
			toast.error({
				title: "Couldn't read the spec",
				description: error instanceof Error ? error.message : undefined,
			})
		} finally {
			setIsUploading(false)
		}
	}

	const createReference = async () => {
		if (!checked) return
		try {
			const { tabId } = await create.mutateAsync({
				name,
				slug: slug.trim(),
				portalInstanceId,
				source: checked.source,
			})
			await utils.apiReference.get.invalidate({ tabId })
			setActiveTabId(tabId)
			close()
			void navigate({ to: "/edit-docs/api/$tabId", params: { tabId } })
			toast.success({ title: API_REFERENCE_CREATED })
		} catch (error) {
			toast.error({
				title: "Couldn't create the API reference",
				description: error instanceof Error ? error.message : undefined,
			})
		}
	}

	const pages = checked
		? checked.preview.operations + checked.preview.events
		: 0

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) close()
			}}
			className="max-w-[34rem]"
			title="New API reference"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={close}>
						Cancel
					</Button>
					{checked ? (
						<Button
							variant="default"
							isLoading={create.isLoading}
							onClick={() => void createReference()}
						>
							{createLabel(pages)}
						</Button>
					) : (
						<Button
							variant="default"
							isLoading={preview.isLoading || isUploading}
							onClick={() => void check()}
						>
							Check spec
						</Button>
					)}
				</div>
			}
		>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700/80">
					<Field label="Source">
						<SpecSourceKindControl
							name="api-reference-source"
							value={kind}
							onValueChange={(value) => {
								setKind(value)
								setChecked(null)
							}}
						/>
					</Field>
					{kind === "url" ? (
						<Field label="OpenAPI URL">
							<Input
								name="new-api-reference-url"
								aria-label="OpenAPI URL"
								value={url}
								onChange={(e) => {
									setUrl(e.target.value)
									setChecked(null)
								}}
								placeholder="https://api.example.com/openapi.json"
								className="w-72 max-sm:w-52"
							/>
						</Field>
					) : (
						<Field label="OpenAPI file">
							<SpecFileButton
								label={file ? "Replace" : "Choose"}
								fileName={file?.name}
								onFile={(next) => {
									if (next.size > MAX_FILE_BYTES) {
										toast.error({ title: SPEC_TOO_LARGE })
										return
									}
									setFile(next)
									setChecked(null)
								}}
							/>
						</Field>
					)}
					<Field label="Tab name">
						<Input
							name="new-api-reference-name"
							aria-label="Tab name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							maxLength={40}
							className="w-72 max-sm:w-52"
						/>
					</Field>
					<Field label="Docs path">
						<Input
							name="new-api-reference-slug"
							aria-label="Docs path"
							prefix="/docs/"
							value={slug}
							onChange={(e) =>
								setSlug(
									e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
								)
							}
							maxLength={50}
							className="w-72 max-sm:w-52"
						/>
					</Field>
				</div>
				{checked && (
					<div className="flex flex-col gap-2 rounded-lg bg-gray-50 px-3 py-2.5 ring-1 ring-black/5 dark:bg-gray-800/60 dark:ring-white/10">
						<div className="text-sm font-medium text-primary">
							{checked.preview.title}
							{checked.preview.apiVersion && (
								<span className="ml-1.5 font-normal text-tertiary">
									{checked.preview.apiVersion}
								</span>
							)}
						</div>
						<div className="grid grid-cols-3 gap-2 text-xs">
							<Stat
								label="Format"
								value={`OpenAPI ${checked.preview.openapi}`}
							/>
							<Stat
								label="Endpoints"
								value={String(checked.preview.operations)}
							/>
							<Stat
								label="Webhook events"
								value={String(checked.preview.events)}
							/>
						</div>
						{checked.preview.warnings.length > 0 && (
							<ul className="list-disc space-y-0.5 pl-4 text-xs text-secondary">
								{checked.preview.warnings.slice(0, 5).map((warning) => (
									<li key={warning}>{warning}</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		</Dialog>
	)
})

function Field({ label, children }: { label: string; children: ReactNode }) {
	return (
		<SettingItem
			className="py-3"
			// A phone narrows the input beside it instead of wrapping the label.
			title={<span className="whitespace-nowrap">{label}</span>}
			action={children}
		/>
	)
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col">
			<span className="text-tertiary">{label}</span>
			<span className="text-primary">{value}</span>
		</div>
	)
}
